import { useFlags } from '@atlaskit/flag';
import Toggle from '@atlaskit/toggle';
import Tooltip from '@atlaskit/tooltip';
import { ICustomerNote } from 'core/api/customers/customers-api-interface';
import CustomersApiService from 'core/api/customers/customers-api.service';
import { useAuthState } from 'core/providers/AuthProvider';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { hasRole } from 'core/utilities/permission-helpers';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Info } from 'react-feather';
import SharedButton from 'shared/components/buttons/button';
import SharedConfirmationDialog from 'shared/components/dialogs/confirmation-dialog/confirmation-dialog';

interface ICustomerOverviewNote {
  note: ICustomerNote;
  editNote: (note: ICustomerNote) => void;
}

const CustomerOverviewNote = ({ note, editNote }: ICustomerOverviewNote) => {
  const { userData, userRoles } = useAuthState();
  const isSuperAdmin = hasRole(['superAdmin'], userRoles);
  const dialog = useDialog();
  const [deletingNote, setDeletingNote] = useState(false);
  const flags = useFlags();

  const deleteNote = async () => {
    setDeletingNote(true);
    try {
      await CustomersApiService.deleteCustomerNote(note.customerUid, note.uid);
      showSuccessFlag('Deletion successful', 'The note was successfully deleted', flags);
      dialog?.closeDialog();
      setDeletingNote(false);
    } catch (error) {
      setDeletingNote(false);
      showErrorFlag('Deletion failed', 'Unable to delete this note, please try again', flags);
    }
  };

  const handleAlertChange = async (alertEnabled: boolean) => {
    try {
      await CustomersApiService.updateCustomerNote({
        uid: note.uid,
        customerUid: note.customerUid,
        alertEnabled,
      });
      showSuccessFlag('Alert status updated successful', 'The alert status was successfully changed', flags);
      dialog?.closeDialog();
    } catch (error) {
      showErrorFlag('Alert status update failed', 'Unable to update the alert status, please try again', flags);
    }
  };

  return (
    <div key={note.uid} className='p-4 border-b last:border-0 body-03'>
      <p className='body-01 whitespace-pre-wrap'>{note.content}</p>
      <div className='flex space-x-1 items-center flex-wrap mt-1'>
        <p className='text-gray-500'>
          {`Created ${dayjs(note.createdAt.toDate()).format('DD/MM/YYYY [at] HH:mm')} by ${note.authorName}`}
        </p>
        {note.editDetail && (
          <Tooltip
            content={`${dayjs(note.editDetail.updatedAt.toDate()).format('DD/MM/YYYY [at] HH:mm')} by ${
              note.editDetail.editorName
            }`}
          >
            {(tooltipProps) => (
              <p className='text-gray-500' {...tooltipProps}>
                • Edited
              </p>
            )}
          </Tooltip>
        )}
      </div>
      <div className='mt-3 flex items-center justify-between'>
        <div className='flex items-center space-x-4'>
          {isSuperAdmin && (
            <SharedButton
              type='button'
              appearance='link'
              label='Delete'
              spacing='none'
              onClick={() =>
                dialog?.openDialog(
                  <SharedConfirmationDialog
                    loading={deletingNote}
                    title='Delete note'
                    textContent='Are you sure you want to delete this note? This action is irreversible.'
                    confirmButton={{
                      appearance: 'danger',
                      label: 'Delete',
                      onClick: () => {
                        deleteNote();
                      },
                    }}
                  />
                )
              }
            />
          )}
          {(note.authorUid === userData?.uid || isSuperAdmin) && (
            <SharedButton type='button' appearance='link' label='Edit' spacing='none' onClick={() => editNote(note)} />
          )}
        </div>
        <div className='flex justify-between items-center'>
          <Tooltip
            content={<p>When alert is enabled, this note will pop-up whenever a user opens this patient file</p>}
          >
            {(tooltipProps) => (
              <div className='flex items-center space-x-1 mr-1' {...tooltipProps}>
                <p>Alert</p>
                <Info className='text-gray-500' size={14} />
              </div>
            )}
          </Tooltip>

          <Toggle onChange={(e) => handleAlertChange(e.currentTarget.checked)} isChecked={note.alertEnabled ?? false} />
        </div>
      </div>
    </div>
  );
};

export default CustomerOverviewNote;
